import React, { useMemo, useState } from "react";
import { ArrowLeft, Sprout, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import SeedIntentRow from "@/modules/racket_lab_admin/SeedIntentRow";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsSeedHelper — RL-SEED-MEMBERSHIPS-HELPER runtime.
 *
 * Read-only discovery aid. Joins `racket_lab_pilot_seed_runs` with
 * `app_memberships` (server-side) and exposes one intent per seeded
 * operator. The page contains NO Apply UI and NO batch action — every
 * row only offers a "Load into Promote form" button that navigates to
 * the S6 page via React Router state. The operator must still run
 * Dry-run + typed-confirm "APPLY" on the Promote page.
 *
 * Track A only (TEAMSOFTWAREINNOVATIONS).
 */
const STATUS_KEYS = ["ready", "already_active", "role_mismatch", "revoked", "missing_user", "pending"];
const ROLE_OPTIONS = ["all", "operator", "shop_manager"];

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex flex-col items-start rounded-md border border-slate-200 bg-white px-3 py-2">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800">{value ?? 0}</span>
  </div>
);

const RLAdminOpsSeedHelper = () => {
  const t = useRacketLabAdminT();
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  const params = useMemo(() => {
    const p = { organisation_id: "TEAMSOFTWAREINNOVATIONS" };
    if (statusFilter !== "all") p.status_filter = statusFilter;
    return p;
  }, [statusFilter]);

  const { data, loading, error, reload } = useAdminApi(
    "/admin/racket-lab/_ops/seed-membership-intents",
    { params },
  );

  const rows = useMemo(() => {
    const all = (data?.intents) || [];
    if (roleFilter === "all") return all;
    return all.filter((r) => r?.intent?.role === roleFilter);
  }, [data, roleFilter]);

  const counts = data?.counts || {};

  return (
    <div data-testid="rl-admin-ops-seed-helper-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <Link
        to="/admin/racket-lab/ops"
        data-testid="rl-ops-seed-helper-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}
      </Link>

      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-xl font-semibold text-slate-900">
            <Sprout className="w-5 h-5 text-emerald-600" /> {t("ops.seed_helper.title")}
          </h1>
          <p className="text-xs text-slate-500">{t("ops.seed_helper.subtitle")}</p>
        </div>
        <button
          type="button"
          data-testid="rl-ops-seed-helper-reload"
          onClick={reload}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50"
        >
          <RefreshCw className="w-3 h-3" /> {t("ops.seed_helper.reload_btn")}
        </button>
      </header>

      <section data-testid="rl-ops-seed-helper-stats" className="flex flex-wrap gap-2">
        {/* Stat labels mirror backend payload field names (`total`,
            `ready`, `already_active`, `role_mismatch`, `revoked`,
            `missing_user`, `pending`). Per HARD rules they remain raw
            so operators map UI ↔ API one-to-one in audit. */}
        <Stat label="total"          value={counts.total}          testId="rl-ops-seed-helper-stat-total" />
        <Stat label="ready"          value={counts.ready}          testId="rl-ops-seed-helper-stat-ready" />
        <Stat label="already_active" value={counts.already_active} testId="rl-ops-seed-helper-stat-already-active" />
        <Stat label="role_mismatch"  value={counts.role_mismatch}  testId="rl-ops-seed-helper-stat-role-mismatch" />
        <Stat label="revoked"        value={counts.revoked}        testId="rl-ops-seed-helper-stat-revoked" />
        <Stat label="missing_user"   value={counts.missing_user}   testId="rl-ops-seed-helper-stat-missing-user" />
        <Stat label="pending"        value={counts.pending}        testId="rl-ops-seed-helper-stat-pending" />
      </section>

      <section className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800">
        <strong>{t("ops.seed_helper.defensive_note_label")}</strong> {t("ops.seed_helper.defensive_note_body")}
      </section>

      {/* Filters */}
      <section data-testid="rl-ops-seed-helper-filters" className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white p-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{t("ops.seed_helper.filter_status_label")}</label>
          <select
            data-testid="rl-ops-seed-helper-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
          >
            <option value="all">all</option>
            {STATUS_KEYS.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{t("ops.seed_helper.filter_role_label")}</label>
          <select
            data-testid="rl-ops-seed-helper-filter-role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
          >
            {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </div>
        <div className="flex flex-col gap-1 text-[11px] text-slate-500">
          <span><strong>{t("ops.seed_helper.seed_run_id_label")}</strong> <code data-testid="rl-ops-seed-helper-run-id">{data?.seed_run_id || "—"}</code></span>
          <span><strong>{t("ops.seed_helper.seed_run_at_label")}</strong> <code>{data?.seed_run_at || "—"}</code></span>
        </div>
      </section>

      {/* Table */}
      <section className="rounded-md border border-slate-200 bg-white">
        {loading && (
          <p data-testid="rl-ops-seed-helper-loading" className="p-4 text-xs text-slate-500">{t("ops.seed_helper.loading")}</p>
        )}
        {error && (
          <p data-testid="rl-ops-seed-helper-error" className="p-4 text-xs text-rose-700">
            {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
          </p>
        )}
        {!loading && !error && data?.no_seed_run_found && (
          <p data-testid="rl-ops-seed-helper-empty" className="p-4 text-xs text-slate-500">
            {(() => {
              const tpl = t("ops.seed_helper.no_seed_run_msg", { org: "__ORG__" });
              const [pre, rest = ""] = tpl.split("__ORG__");
              return (<><span>{pre}</span><code>TEAMSOFTWAREINNOVATIONS</code><span>{rest}</span></>);
            })()}
          </p>
        )}
        {!loading && !error && !data?.no_seed_run_found && rows.length === 0 && (
          <p data-testid="rl-ops-seed-helper-empty-filtered" className="p-4 text-xs text-slate-500">{t("ops.seed_helper.no_match_filters")}</p>
        )}
        {!loading && !error && rows.length > 0 && (
          <table data-testid="rl-ops-seed-helper-table" className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.user_id")}</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.proposed_role")}</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.shop_ids")}</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.status")}</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.current")}</th>
                <th className="px-2 py-2 text-left">{t("ops.seed_helper.columns.action")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (<SeedIntentRow key={row.row_index} row={row} />))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default RLAdminOpsSeedHelper;
